import React, { useEffect, useRef, useState } from 'react';
import { useFrame } from '@react-three/fiber';
import { Text, Billboard } from '@react-three/drei';
import * as THREE from 'three';
import { useWorldStore, ZOOM } from '../state/worldStore';
import { TOWN_LOCATIONS, ENTRY_VIA_PORTAL } from '../rooms';
import { useVenueAnchors } from '../hooks/useVenueAnchors';

// A clickable proxy over a venue's whole building: click anywhere on it to
// enter, and it glows on hover so it's clearly interactive.
//
// This IS the "entire building is the hit zone" that ENTRY_VIA_PORTAL retires —
// a box the size of the venue that opens it from any face, roof included. With
// doors on it draws nothing at all rather than staying as a dead hover glow,
// which would advertise an interaction that no longer happens.
function VenueBuilding({ box, accent, interiorId }) {
  const enterInterior = useWorldStore((s) => s.enterInterior);
  const [hover, setHover] = useState(false);
  if (!interiorId || ENTRY_VIA_PORTAL) return null;
  return (
    <group position={box.center}>
      <mesh
        onClick={(e) => { e.stopPropagation(); enterInterior(interiorId); }}
        onPointerOver={(e) => { e.stopPropagation(); setHover(true); document.body.style.cursor = 'pointer'; }}
        onPointerOut={() => { setHover(false); document.body.style.cursor = 'auto'; }}
      >
        <boxGeometry args={box.size} />
        <meshBasicMaterial transparent opacity={0} depthWrite={false} />
      </mesh>
      {hover && (
        <mesh scale={[1.02, 1.02, 1.02]}>
          <boxGeometry args={box.size} />
          <meshBasicMaterial color={accent} transparent opacity={0.18} depthWrite={false} side={THREE.BackSide} blending={THREE.AdditiveBlending} toneMapped={false} />
        </mesh>
      )}
    </group>
  );
}

// VenueMarkers — a small billboarded sign hovering just above each venue's
// building (anchor y = the building's measured roof height), so it reads as
// "this building" without being lost high in the sky. Click the disc to step
// inside. Venues are spread across the city (see CityModel), so signs don't
// overlap.

const ROOF_MARGIN = 3; // sit this far above the building's roofline

// ── THE BEACON'S GLOW IS A HALO, NOT A LIGHT ────────────────────────────────
// Each marker used to carry a <pointLight> (intensity 4, reach 12). Eight of
// them, and in three.js a point light is paid for by EVERY lit pixel in the
// scene, not just the roof it hangs over — every building, road and billboard
// in the city looped over all eight, every frame, for a tint on a rooftop you
// mostly see from the street as a coloured smudge. The halo gives the beacon
// the same "this one is lit" read at night for the cost of one soft quad.
let HALO_TEX = null;
function haloTexture() {
  if (HALO_TEX) return HALO_TEX;
  const c = document.createElement('canvas'); c.width = c.height = 128;
  const g = c.getContext('2d');
  const grad = g.createRadialGradient(64, 64, 0, 64, 64, 64);
  grad.addColorStop(0, 'rgba(255,255,255,0.85)');
  grad.addColorStop(0.35, 'rgba(255,255,255,0.32)');
  grad.addColorStop(1, 'rgba(255,255,255,0)');
  g.fillStyle = grad; g.fillRect(0, 0, 128, 128);
  HALO_TEX = new THREE.CanvasTexture(c);
  HALO_TEX.colorSpace = THREE.SRGBColorSpace;
  return HALO_TEX;
}

// A marker is sized to be read from across the city — which is exactly wrong
// when you are standing under it: beside a low building the sign hangs at eye
// level and fills a quarter of the screen (the Underground's does, from the
// pavement outside). So it shrinks as you close in — full size past FULL_AT,
// down to MIN_SCALE by NEAR_AT — and stays put from across town, where it has
// the job of being found.
const FULL_AT = 60;
const NEAR_AT = 8;
const MIN_SCALE = 0.22;

function Beacon({ pos, name, accent, interiorId }) {
  const MARK_Y = pos[1] + ROOF_MARGIN;
  const enterInterior = useWorldStore((s) => s.enterInterior);
  const disc = useRef();
  const sign = useRef();
  useFrame(({ camera }) => {
    const g = sign.current;
    if (!g) return;
    const d = Math.hypot(camera.position.x - pos[0], camera.position.y - MARK_Y, camera.position.z - pos[2]);
    const k = Math.max(0, Math.min(1, (d - NEAR_AT) / (FULL_AT - NEAR_AT)));
    const sc = MIN_SCALE + (1 - MIN_SCALE) * k;
    if (Math.abs(g.scale.x - sc) > 0.005) g.scale.setScalar(sc);
  });
  // STATIC ON PURPOSE (Khari, 2026-08-02: "the markers on the building are
  // pulsing.. wasted compute"). The sine pulse ran a useFrame + material write
  // per marker per frame for an effect nobody needed — a beacon reads as a
  // beacon at a fixed opacity. If it ever comes back, it should be a shader
  // uniform on a shared material, not per-marker JS.
  useEffect(() => {
    if (disc.current) disc.current.material.opacity = 0.85;
  }, []);
  // THE SIGN STAYS A WAY IN, deliberately, and is the one exception to
  // ENTRY_VIA_PORTAL.
  //
  // It is not a facade. A marker is wayfinding — the same category as the map,
  // which also still enters — and it is explicitly presented as UI rather than
  // as part of the city. What that rule retires is the pretence that a whole
  // building is a button: a box you can click on the roof, through a wall, or
  // from the far side of the block, so a venue has no front and nothing can be
  // stationed at it.
  //
  // Nothing is bypassed by keeping it. The check lives in enterInterior, so a
  // marker click into a gated venue is refused exactly like walking into the
  // door — which is the whole reason the gate was put in the store rather than
  // on the door the guard happens to stand next to.
  const enter = interiorId ? (e) => { e.stopPropagation(); enterInterior(interiorId); } : undefined;
  const hover = interiorId
    ? { onPointerOver: (e) => { e.stopPropagation(); document.body.style.cursor = 'pointer'; },
        onPointerOut: () => { document.body.style.cursor = 'auto'; } }
    : {};
  return (
    <group position={[pos[0], 0, pos[2]]}>
      {/* short drop-pin to the pavement below the sign */}
      <mesh position={[0, MARK_Y / 2, 0]}>
        <cylinderGeometry args={[0.08, 0.08, MARK_Y, 6]} />
        <meshBasicMaterial color={accent} toneMapped={false} transparent opacity={0.5} />
      </mesh>
      <Billboard ref={sign} position={[0, MARK_Y, 0]}>
        <mesh position={[0, 0, -0.05]} raycast={() => null}>
          <planeGeometry args={[6.5, 6.5]} />
          <meshBasicMaterial map={haloTexture()} color={accent} transparent opacity={0.55} depthWrite={false} blending={THREE.AdditiveBlending} toneMapped={false} />
        </mesh>
        <mesh ref={disc} onClick={enter} {...hover}>
          <circleGeometry args={[1.1, 32]} />
          <meshBasicMaterial color={accent} transparent opacity={0.9} depthWrite={false} toneMapped={false} />
        </mesh>
        <mesh position={[0, 0, 0.01]}>
          <ringGeometry args={[1.1, 1.35, 32]} />
          <meshBasicMaterial color="#ffffff" transparent opacity={0.9} toneMapped={false} />
        </mesh>
        <Text position={[0, 1.9, 0]} fontSize={1.5} color="#ffffff" outlineWidth={0.09} outlineColor="#000000" anchorX="center" anchorY="middle">
          {name}
        </Text>
      </Billboard>
    </group>
  );
}

export default function VenueMarkers() {
  // Through the shared hook so an unsaved placement moves the marker too.
  const venueAnchors = useVenueAnchors();
  const venueBoxes = useWorldStore((s) => s.venueBoxes);
  // NOT INDOORS (2026-10-31, Khari: "the placement pole is still showing when I
  // enter"). The markers are the street's wayfinding, mounted with the city —
  // and the city stays mounted inside a SITED room (the Corner Market, now the
  // Jahva House and HQ), whose anchor IS the room's middle: the drop-pin stood
  // in the shop, and every other venue's sign floated over the roofs outside
  // the window. Inside any room there is nothing to find; they come back on the
  // way out.
  const indoors = useWorldStore((s) => s.zoom === ZOOM.INTERIOR);
  if (!venueAnchors || indoors) return null;
  return (
    <group>
      {TOWN_LOCATIONS.map((loc) => {
        const a = venueAnchors[loc.key];
        if (!a) return null;
        return (
          <group key={loc.key}>
            <Beacon pos={a} name={loc.name} accent={loc.accent} interiorId={loc.interiorId} />
            {venueBoxes && venueBoxes[loc.key] && (
              <VenueBuilding box={venueBoxes[loc.key]} accent={loc.accent} interiorId={loc.interiorId} />
            )}
          </group>
        );
      })}
    </group>
  );
}
